import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { API_STATUS_FAILED } from '@/constants/error-codes';
import { legacyRestFetch } from '@/features/account-settings/legacyRestFetch';
import { getDeviceUUID } from '@/shared/utils/cookieUtils';
import { svgStringToDataUrl } from '@/shared/utils/file';

type UseSetupTwoFactorAuthAppMethodResults = {
  isLoading: boolean;
  /**
   * The TOTP URL that the authentication apps save.
   * Rarely is this shown directly to the user.
   */
  url: string | null;
  /**
   * A raster image data URL of the QR code that can be used to scan the TOTP URL.
   */
  qrCodeDataUrl: string | null;
  /**
   * The secret key that the authentication apps can use to generate the TOTP URL
   */
  secret: string | null;
};

export function useSetupTwoFactorAuthAppMethod(): UseSetupTwoFactorAuthAppMethodResults {
  const [url, setUrl] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);

  useEffect(() => {
    createTotpUrl()
      .then(async (url) => {
        setUrl(url);
        setSecret(getSecretFromUrl(url));
        const svgImg = await QRCode.toString(url, { type: 'svg' });
        const dataUrlSvg = svgStringToDataUrl(svgImg);
        setQrCodeDataUrl(dataUrlSvg);
        setIsLoading(false);
      })
      .catch((error) => {
        throw error;
      });
  }, []);

  return { url, secret, isLoading, qrCodeDataUrl };
}

async function createTotpUrl() {
  const deviceUuid = getDeviceUUID();
  const body = new FormData();
  body.append('device_uuid', deviceUuid);
  const response = await legacyRestFetch('config_totp_device_and_url', {
    method: 'POST',
    body,
  });

  if (!response.ok) {
    throw new TwoFactorAuthSetupError('Server error');
  }

  const data = await response.json();

  if (data.status === API_STATUS_FAILED) {
    throw new TwoFactorAuthSetupError(data.message);
  }

  return data.url as string;
}

function getSecretFromUrl(url: string) {
  const urlObj = new URL(url);
  const secret = urlObj.searchParams.get('secret');
  return secret;
}

class TwoFactorAuthSetupError extends Error {
  name = 'TwoFactorAuthSetupError';
  constructor(message: string) {
    super(`Two-factor authentication setup error: ${message}`);
  }
}
